export interface PatternCoordinates {
	row: number;
	column: number;
}

export interface PatternCell extends PatternCoordinates {
	key: string;
	x: number;
	y: number;
	transform: string;
	entryDelay: number;
	/** Whether the highlight may occupy this cell. */
	reachable: boolean;
}

/** One row of the lattice, sliding along the plane as a unit. */
export interface PatternRail {
	row: number;
	/** `1` slides the rail right along the plane, `-1` slides it left. */
	direction: 1 | -1;
	/** Offset that carries every mouth into its neighbour's slot, so each loop ends where it began. */
	shift: { x: number; y: number };
	cells: PatternCell[];
}

export interface MouthFace {
	path: string;
	/** The large front face carries the panel theme; every other face stays flat primary. */
	main: boolean;
}

export const mouthFaces: MouthFace[] = [
	{
		main: false,
		path: 'M698.83 1378.69L515.093 1258.21L515.093 1485.99L698.83 1378.69Z',
	},
	{
		main: false,
		path: 'M1887.89 479.407L2200.95 660.154L2200.72 1196.96L1889.85 1016.35L1887.89 479.407Z',
	},
	{
		main: false,
		path: 'M550.595 2150.4L242.983 1972.8L1889.87 1017.24L2200.74 1197.85L550.595 2150.4Z',
	},
	{
		main: true,
		path: 'M1604.22 639.708L1887.42 478.869L1889.39 1015.81L242.493 1971.36L242.084 1413.21L515.086 1258.21L515.086 1485.01L1607.89 854.418L1604.22 639.708Z',
	},
];

export const mouthDashes = [
	'M1887.44 1015.82L2200.25 1196.42',
	'M1887.52 481.694L1887.44 1015.82L245.39 1972.34',
];

export const mouthOutline = 'M515.088 1258.21L698.825 1378.5L1607.9 854.418L1604.23 639.708L1887.43 478.869L2200.49 659.616L2200.26 1196.42L550.107 2148.96L242.495 1971.36L242.088 1413.21L515.088 1258.21ZM698.825 1378.5L515.088 1485.8L515.088 1258.21';

/** Bounding box of the mouth artwork in its own coordinate space. */
export const MOUTH_BOUNDS = { minX: 242.084, minY: 479.407, maxX: 2200.95, maxY: 2150.4 };

/** Shrinks the artwork from its own coordinate space down to lattice scale. */
export const MOUTH_SCALE = 0.222;

export const VIEWBOX = { width: 1000, height: 1025 };

const ROWS = 6;
const BACK_ROWS = 2;
const MIN_ROW = -BACK_ROWS;
const MAX_ROW = ROWS - 1;
/** A spare column trails off the left edge so right-moving rails never open a gap behind them. */
const FIRST_COLUMN = -1;
const COLUMNS = 9;

/** Where the walk starts, and the seed of the reachable component. */
export const START_CELL: PatternCoordinates = { row: -2, column: 1 };

export const ENTRY_WAVE_SPAN = 320;

const MOUTH_CENTER = {
	x: (MOUTH_BOUNDS.minX + MOUTH_BOUNDS.maxX) / 2,
	y: (MOUTH_BOUNDS.minY + MOUTH_BOUNDS.maxY) / 2,
};

const MOUTH_HALF_WIDTH = ((MOUTH_BOUNDS.maxX - MOUTH_BOUNDS.minX) / 2) * MOUTH_SCALE;
const MOUTH_HALF_HEIGHT = ((MOUTH_BOUNDS.maxY - MOUTH_BOUNDS.minY) / 2) * MOUTH_SCALE;
const MOUTH_TOP_OFFSET = (MOUTH_BOUNDS.minY - MOUTH_CENTER.y) * MOUTH_SCALE;

const PLANE_ANGLE = Math.PI / 6;
const PLANE_RAIL = { x: Math.cos(PLANE_ANGLE), y: -Math.sin(PLANE_ANGLE) };
const PLANE_DEPTH = { x: Math.cos(PLANE_ANGLE), y: Math.sin(PLANE_ANGLE) };

/** Slides the lattice left so the first column bleeds off the edge instead of sitting flush against it. */
const LATTICE_OFFSET_X = -119;
/** Row-to-row depth spacing as a fraction of the rail pitch; larger values push rows further down the plane. */
const DEPTH_PITCH_RATIO = 0.268;
/** Where the top row lands vertically, as a fraction of the viewBox height. */
const TOP_ROW_ENTRY_RATIO = 0.65;
/** How far neighbouring mouths overlap along the rail, so the lattice reads as a continuous surface. */
const HORIZONTAL_OVERLAP = 12;

const RAIL_PITCH = (MOUTH_HALF_WIDTH * 2 - HORIZONTAL_OVERLAP) / PLANE_RAIL.x;
const DEPTH_PITCH = RAIL_PITCH * DEPTH_PITCH_RATIO;
const ROW_STAGGER = -RAIL_PITCH / 2;
const ROW_SCREEN_Y = ROW_STAGGER * PLANE_RAIL.y + DEPTH_PITCH * PLANE_DEPTH.y;
const TOP_ROW_ENTRY_Y = VIEWBOX.height * TOP_ROW_ENTRY_RATIO;

const LATTICE_ORIGIN = {
	x: LATTICE_OFFSET_X,
	y: TOP_ROW_ENTRY_Y - MOUTH_TOP_OFFSET - MIN_ROW * ROW_SCREEN_Y,
};

const NEIGHBOUR_STEPS: readonly PatternCoordinates[] = [
	{ row: -1, column: 0 },
	{ row: 1, column: 0 },
	{ row: 0, column: -1 },
	{ row: 0, column: 1 },
];

interface PositionedCell extends PatternCoordinates {
	key: string;
	x: number;
	y: number;
	transform: string;
	onScreen: boolean;
	/** On screen at some point of its rail's slide, which is when a pointer can land on it. */
	onScreenWhileSliding: boolean;
}

export function cellKey(row: number, column: number): string {
	return `${row}:${column}`;
}

function projectPlane(rail: number, depth: number) {
	return {
		x: rail * PLANE_RAIL.x + depth * PLANE_DEPTH.x,
		y: rail * PLANE_RAIL.y + depth * PLANE_DEPTH.y,
	};
}

function cellPosition(row: number, column: number) {
	const projected = projectPlane(
		column * RAIL_PITCH + row * ROW_STAGGER,
		row * DEPTH_PITCH,
	);

	return {
		x: LATTICE_ORIGIN.x + projected.x,
		y: LATTICE_ORIGIN.y + projected.y,
	};
}

/** Where a cell sits once its rail has slid a full slot. */
function slidPosition(position: { x: number; y: number }, row: number) {
	const shift = projectPlane(railDirection(row) * RAIL_PITCH, 0);
	return { x: position.x + shift.x, y: position.y + shift.y };
}

/** Whether the cell's bounding box overlaps the nominal viewBox. */
function isOnScreen(position: { x: number; y: number }): boolean {
	return position.x + MOUTH_HALF_WIDTH > 0
		&& position.x - MOUTH_HALF_WIDTH < VIEWBOX.width
		&& position.y + MOUTH_HALF_HEIGHT > 0
		&& position.y - MOUTH_HALF_HEIGHT < VIEWBOX.height;
}

function positionCells(): PositionedCell[] {
	const cells: PositionedCell[] = [];

	for (let row = MIN_ROW; row <= MAX_ROW; row += 1) {
		for (let column = FIRST_COLUMN; column < COLUMNS; column += 1) {
			const position = cellPosition(row, column);

			cells.push({
				row,
				column,
				key: cellKey(row, column),
				x: position.x,
				y: position.y,
				transform: `translate(${position.x - MOUTH_CENTER.x * MOUTH_SCALE} ${position.y - MOUTH_CENTER.y * MOUTH_SCALE}) scale(${MOUTH_SCALE})`,
				onScreen: isOnScreen(position),
				onScreenWhileSliding: isOnScreen(position) || isOnScreen(slidPosition(position, row)),
			});
		}
	}

	return cells;
}

/**
 * Cells the highlight may occupy: the connected component of cells on screen at some point of their
 * rail's slide containing `START_CELL`. Counting the whole slide lets a pointer highlight a mouth that
 * has slid into view; cells cut off from that component can never be walked to or away from, so a
 * highlight that entered one would be stranded there.
 */
function findReachableKeys(cells: PositionedCell[]): Set<string> {
	const onScreenKeys = new Set(cells.filter((cell) => cell.onScreenWhileSliding).map((cell) => cell.key));
	const startKey = cellKey(START_CELL.row, START_CELL.column);
	const reachableKeys = new Set<string>();
	if (!onScreenKeys.has(startKey)) return reachableKeys;

	const queue: PatternCoordinates[] = [START_CELL];
	reachableKeys.add(startKey);

	for (let head = 0; head < queue.length; head += 1) {
		const cell = queue[head];
		if (!cell) continue;

		for (const step of NEIGHBOUR_STEPS) {
			const neighbour = { row: cell.row + step.row, column: cell.column + step.column };
			const neighbourKey = cellKey(neighbour.row, neighbour.column);
			if (reachableKeys.has(neighbourKey) || !onScreenKeys.has(neighbourKey)) continue;
			reachableKeys.add(neighbourKey);
			queue.push(neighbour);
		}
	}

	return reachableKeys;
}

function createPatternCells(): PatternCell[] {
	const positioned = positionCells();
	const reachableKeys = findReachableKeys(positioned);

	// The entry wave is paced against the cells inside the viewBox, so the visible rows carry the
	// full span no matter how far the lattice bleeds past the edges.
	const onScreen = positioned.filter((cell) => cell.onScreen);
	const entryBottom = Math.max(...onScreen.map((cell) => cell.y));
	const entryTop = Math.min(...onScreen.map((cell) => cell.y));
	const entryRange = Math.max(1, entryBottom - entryTop);

	return positioned
		.map((cell) => ({
			key: cell.key,
			row: cell.row,
			column: cell.column,
			x: cell.x,
			y: cell.y,
			transform: cell.transform,
			entryDelay: Math.round(Math.max(0, Math.min(1, (entryBottom - cell.y) / entryRange)) * ENTRY_WAVE_SPAN),
			reachable: reachableKeys.has(cell.key),
		}))
		.toSorted((a, b) => b.row - a.row || a.column - b.column);
}

export const patternCells: PatternCell[] = createPatternCells();

/** Rails alternate direction, starting with the top rail sliding right. */
function railDirection(row: number): 1 | -1 {
	return (row - MIN_ROW) % 2 === 0 ? 1 : -1;
}

function createPatternRails(): PatternRail[] {
	const rails = new Map<number, PatternRail>();

	for (const cell of patternCells) {
		let rail = rails.get(cell.row);

		if (!rail) {
			const direction = railDirection(cell.row);
			rail = { row: cell.row, direction, shift: projectPlane(direction * RAIL_PITCH, 0), cells: [] };
			rails.set(cell.row, rail);
		}

		rail.cells.push(cell);
	}

	// Map insertion follows `patternCells`, so rails keep its back-to-front paint order.
	return [...rails.values()];
}

export const patternRails: PatternRail[] = createPatternRails();

export const reachableCells: PatternCell[] = patternCells.filter((cell) => cell.reachable);

const reachableCellsByKey = new Map(reachableCells.map((cell) => [cell.key, cell]));

/** Key of the cell whose entry animation finishes last, so the entry state is cleared exactly once. */
export const LAST_ENTRY_KEY = patternCells.reduce((last, cell) => (cell.entryDelay >= last.entryDelay ? cell : last)).key;

export function getCell(row: number, column: number): PatternCell | undefined {
	return reachableCellsByKey.get(cellKey(row, column));
}

/**
 * Steps from `from` to `to`, excluding `from` and including `to`. Empty when `to` cannot be walked to.
 * `from` may sit outside the reachable cells (a rail can carry the highlight there); every step lands inside them.
 */
export function findPath(from: PatternCoordinates, to: PatternCoordinates): PatternCoordinates[] {
	const fromKey = cellKey(from.row, from.column);
	const toKey = cellKey(to.row, to.column);
	if (fromKey === toKey) return [];
	if (!reachableCellsByKey.has(toKey)) return [];

	const cameFrom = new Map<string, string>();
	const queue: PatternCoordinates[] = [{ row: from.row, column: from.column }];
	const seen = new Set([fromKey]);

	for (let head = 0; head < queue.length; head += 1) {
		const cell = queue[head];
		if (!cell) continue;
		if (cellKey(cell.row, cell.column) === toKey) break;

		for (const step of NEIGHBOUR_STEPS) {
			const neighbour = { row: cell.row + step.row, column: cell.column + step.column };
			const neighbourKey = cellKey(neighbour.row, neighbour.column);
			if (seen.has(neighbourKey) || !reachableCellsByKey.has(neighbourKey)) continue;
			seen.add(neighbourKey);
			cameFrom.set(neighbourKey, cellKey(cell.row, cell.column));
			queue.push(neighbour);
		}
	}

	const path: PatternCoordinates[] = [];

	for (let key = toKey; key !== fromKey;) {
		const cell = reachableCellsByKey.get(key);
		const previousKey = cameFrom.get(key);
		if (!cell || previousKey === undefined) return [];

		path.unshift({ row: cell.row, column: cell.column });
		key = previousKey;
	}

	return path;
}
